"use client";

import { ArrowUpRight, Blocks, BrainCircuit, Lock } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
export default function Capabilities() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const pillars = [{
    number: "01",
    title: "PRECISION ENGINEERING",
    icon: Blocks,
    description: "We reject standard manufacturing as a default. Every component moves through rigorous quality control environments engineered for industrial excellence — high-tolerance machining, advanced material testing, and architectural precision. No shortcuts. No compromises. Only systems designed around your project's structural integrity and performance."
  }, {
    number: "02",
    title: "DATA-DRIVEN DESIGN",
    icon: BrainCircuit,
    description: "Our methodology is grounded in advanced computational science. Every phase — from concept to deployment — is a structured opportunity for optimization. Predictive modeling, real-time telemetry, and individualized protocols build efficiency that lasts long after the implementation."
  }, {
    number: "03",
    title: "TOTAL INTEGRITY",
    icon: Lock,
    description: "Twenty-four hour monitoring. Encrypted access controls. Redundant power, network, and operational response systems. EVERMARK operates as a fortified facility where discretion and reliability are absolute — engineered to the standards our global clientele expect everywhere else."
  }];
  return <section data-section-id="5200" id="capabilities" className="bg-background text-foreground py-32 md:py-50 px-6 md:px-12 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-16 md:mb-24 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-4">
            <div className="flex flex-col gap-3">
              <span className="font-mono text-xs md:text-sm tracking-widest uppercase text-primary">
                02 / OUR CAPABILITIES
              </span>
              <div className="h-px w-16 bg-primary" />
            </div>
          </div>
          <div className="lg:col-span-8">
            <h2 className="font-default font-black text-4xl sm:text-5xl md:text-6xl lg:text-[88px] leading-[0.95] tracking-tight uppercase">
              THREE /
              <br />
              CORE PILLARS.
            </h2>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <PillarCard key={i} pillar={pillar} Icon={Icon} index={i} />;
        })}
        </div>
      </div>
    </section>;
}
function PillarCard({
  pillar,
  Icon,
  index
}: {
  pillar: {
    number: string;
    title: string;
    description: string;
  };
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} data-index={index} style={{
    transitionDelay: `${index * 100}ms`
  }} className={`transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
      <Card className="group relative h-full min-h-[480px] bg-card backdrop-blur-xl border border-border rounded-lg p-10 md:p-12 flex flex-col shadow-lg hover:border-primary hover:shadow-2xl transition-all duration-500">
        {}
        <div className="font-mono text-5xl md:text-6xl font-light text-primary transition-transform duration-500 group-hover:scale-110 origin-left">
          {pillar.number}
        </div>

        {}
        <div className="h-px w-full bg-border mt-6 mb-10 group-hover:bg-primary transition-colors duration-500" />

        {}
        <Icon className="size-8 text-foreground/70 group-hover:text-primary transition-colors duration-500 mb-8" strokeWidth={1} />

        {}
        <h3 className="font-default font-bold text-2xl md:text-3xl uppercase tracking-tight mb-6 leading-tight">
          {pillar.title}
        </h3>

        {}
        <p className="font-default text-base md:text-[17px] leading-relaxed text-foreground/75 mb-10 flex-grow">
          {pillar.description}
        </p>

        {}
        
      </Card>
    </div>;
}